import { Button, Card, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Tabs, Typography, message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { currentSiteDomain, instanceUrls, pickSiteUrl, toPublicHost, visibleInstanceUrls } from "../core/site-domain";
import { serverFetch } from "../core/server";
import { sessionFromApi } from "../store/session";
import { setUserSession } from "../store/user-slice";
import DatabaseTab from "./database/database-tab";
import UsersEditor from "./users-editor";
import "./cabinet-page.css";

const STATUS_LABEL = {
  notInstalled: "Не установлено",
  mock: "Создано (мок, Windows)",
  running: "Работает",
  stopped: "Остановлено",
  error: "Ошибка"
};

const CabinetPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector((state) => state.user.session);
  const [form] = Form.useForm();
  const [settings, setSettings] = useState(null);
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [license, setLicense] = useState(null);
  const [savingLicense, setSavingLicense] = useState(false);

  const nameValue = Form.useWatch("name", form);
  const siteDomain = currentSiteDomain();

  const load = useCallback(() => {
    if (!session?.companyId) {
      setSettings(null);
      setApp(null);
      setLicense(null);
      return;
    }

    setLoading(true);
    Promise.all([
      serverFetch("companies/settings"),
      serverFetch("app"),
      session?.isSuperAdmin ? serverFetch("app/license").catch(() => null) : Promise.resolve(null)
    ])
      .then(([company, appData, licenseData]) => {
        setSettings(company);
        setApp(appData);
        setLicense(licenseData);
        form.setFieldsValue({ name: company.instanceName || "" });
      })
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось загрузить кабинет"))
      .finally(() => setLoading(false));
  }, [form, session?.companyId, session?.isSuperAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const name = nameValue?.trim().toLowerCase();
    if (!name) {
      setAvailability(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      serverFetch("instances/available", { queryParams: { name } })
        .then(setAvailability)
        .catch(() => setAvailability(null));
    }, 300);

    return () => clearTimeout(timer);
  }, [nameValue]);

  const domainLocked = app && (app.status === "running" || app.status === "stopped");

  const onSaveDomain = (values) => {
    setSaving(true);
    serverFetch("companies/domain", {
      method: "PUT",
      bodyData: { name: values.name.trim() }
    })
      .then((data) => {
        message.success("Домен сохранён");
        setSettings(data);
        form.setFieldsValue({ name: data.instanceName || "" });
      })
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось сохранить домен"))
      .finally(() => setSaving(false));
  };

  const onCreate = () => {
    const name = (form.getFieldValue("name") || settings?.instanceName || "").trim();
    setCreating(true);
    serverFetch("app", {
      method: "POST",
      bodyData: { name }
    })
      .then((data) => {
        message.success(data.mock ? "Копия создана (мок на Windows)" : "Приложение создано");
        setApp(data);
        load();
      })
      .catch((e) => message.error(e.userMessage || e.message || "Не удалось создать приложение"))
      .finally(() => setCreating(false));
  };

  const onDelete = () => {
    if (!settings?.id) return;
    Modal.confirm({
      title: "Удалить компанию?",
      content: settings.domain
        ? `${settings.name} (${toPublicHost(settings.domain)}). Пользователи компании тоже будут удалены.`
        : `${settings.name}. Пользователи компании тоже будут удалены.`,
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: () => {
        setDeleting(true);
        return serverFetch(`companies/${settings.id}`, { method: "DELETE" })
          .then(() => serverFetch("auth/checksession"))
          .then((sessionData) => {
            const sessionId = localStorage.getItem("sessionId");
            dispatch(setUserSession(sessionFromApi(sessionData, sessionId)));
            message.success("Компания удалена");
            navigate("/cabinet/companies");
          })
          .catch((e) => message.error(e.userMessage || e.message || "Не удалось удалить компанию"))
          .finally(() => setDeleting(false));
      }
    });
  };

  if (session?.isSuperAdmin && !session.companyId) {
    return (
      <div className="cabinet">
        <Typography.Title level={2}>Приложение</Typography.Title>
        <Typography.Paragraph>
          Эталон справочников —{" "}
          <Link to="/cabinet/default">база по умолчанию</Link>
          {" "}(<code>default.webokna.online</code>). Чтобы смотреть копию компании, откройте{" "}
          <Link to="/cabinet/companies">список компаний</Link>.
        </Typography.Paragraph>
      </div>
    );
  }

  const previewName = nameValue?.trim().toLowerCase();
  const previewUrls = previewName ? instanceUrls(previewName) : [];
  const readyUrls = settings?.urls?.length ? settings.urls : app?.urls?.length ? app.urls : [];
  const primaryUrl = pickSiteUrl(readyUrls, settings?.url || app?.url);
  const canCreate =
    (availability?.ok || !!settings?.instanceName) &&
    app?.status !== "running" &&
    app?.status !== "stopped";
  const appCreated = !!(app?.status && app.status !== "notInstalled");

  const mainCards = (
    <>
      <Card className="cabinet-card" title="Домен" loading={loading}>
        {domainLocked ? (
          <InstanceLinks urls={readyUrls} fallback={primaryUrl} />
        ) : (
          <Form form={form} layout="vertical" onFinish={onSaveDomain}>
            <Form.Item
              name="name"
              label="Поддомен"
              extra={
                availability && !availability.ok
                  ? availability.reason
                  : availability?.ok
                    ? "Имя свободно"
                    : undefined
              }
              validateStatus={availability && !availability.ok ? "error" : undefined}
              rules={[{ required: true, message: "Укажите поддомен" }]}
            >
              <Input
                className="cabinet-domain-input"
                addonBefore="https://"
                addonAfter={`.${siteDomain}`}
                placeholder="company"
                disabled={saving}
              />
            </Form.Item>
            {previewUrls.length > 0 && <InstanceLinks urls={previewUrls} />}
            {session?.isAdmin && (
              <div className="form-footer-actions">
                <Button type="primary" htmlType="submit" loading={saving} disabled={!availability?.ok}>
                  Сохранить
                </Button>
              </div>
            )}
          </Form>
        )}
      </Card>

      <Card className="cabinet-card" title="Статус приложения" loading={loading}>
        <Typography.Paragraph className="cabinet-status">
          Статус: <strong>{STATUS_LABEL[app?.status] || "—"}</strong>
        </Typography.Paragraph>
        {app?.status && app.status !== "notInstalled" && (
          <InstanceLinks urls={readyUrls} fallback={primaryUrl} />
        )}
        {session?.isAdmin && canCreate && (
          <Button type="primary" onClick={onCreate} loading={creating} disabled={!previewName && !settings?.instanceName}>
            Создать приложение
          </Button>
        )}
      </Card>

      {session?.isSuperAdmin && (
        <Card className="cabinet-card" title="Пользователи">
          <UsersEditor listUrl="users" canEdit showAdminFlag />
        </Card>
      )}
    </>
  );

  const licenseCard = (
    <Card className="cabinet-card" title="Лицензии" loading={loading}>
      <LicenseEditor
        license={license}
        installed={!!(app?.status && app.status !== "notInstalled")}
        canEdit
        saving={savingLicense}
        onSave={(values) => {
          setSavingLicense(true);
          return serverFetch("app/license", {
            method: "PUT",
            bodyData: values
          })
            .then((data) => {
              setLicense(data);
              message.success("Лицензия сохранена");
            })
            .catch((e) => {
              message.error(e.userMessage || e.message || "Не удалось сохранить лицензию");
            })
            .finally(() => setSavingLicense(false));
        }}
      />
    </Card>
  );

  return (
    <div className="cabinet">
      <Typography.Title level={2}>Приложение</Typography.Title>
      <Typography.Paragraph type="secondary" className="cabinet-company-line">
        Компания: <strong>{settings?.name || session?.companyName || "—"}</strong>
        {session?.isSuperAdmin && settings?.id ? (
          <Button danger size="small" loading={deleting} onClick={onDelete}>
            Удалить
          </Button>
        ) : null}
      </Typography.Paragraph>

      <Tabs
        className="cabinet-tabs"
        defaultActiveKey="main"
        items={[
          { key: "main", label: "Основные", children: mainCards },
          { key: "database", label: "База", disabled: !appCreated, children: appCreated ? <DatabaseTab /> : null },
          ...(session?.isSuperAdmin
            ? [{ key: "license", label: "Лицензии", disabled: !appCreated, children: appCreated ? licenseCard : null }]
            : [])
        ]}
      />
    </div>
  );
};

const DATE_FORMAT = "DD.MM.YYYY";

const toDayjs = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const toDateValue = (value) => (value ? value.format("YYYY-MM-DD") : null);

const licenseFormValues = (license) => ({
  licenseNumber: license?.licenseNumber || "",
  date: toDayjs(license?.date) || dayjs(),
  expirationDate: toDayjs(license?.expirationDate),
  modules: (license?.modules || []).filter((item) => item.enabled).map((item) => item.id),
  extraModules: (license?.extraModules || []).filter((item) => item.enabled).map((item) => item.id),
  workplaces: license?.workplaces ?? null,
  dealerWorkplaces: license?.dealerWorkplaces ?? null,
  databases: license?.databases ?? 1
});

const LicenseEditor = ({ license, installed, canEdit, saving, onSave }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (license) form.setFieldsValue(licenseFormValues(license));
  }, [form, license]);

  if (!installed) {
    return (
      <Typography.Paragraph type="secondary">
        Сначала создайте приложение — лицензия хранится в файле копии <code>license.json</code>.
      </Typography.Paragraph>
    );
  }

  if (!license) {
    return <Typography.Paragraph type="secondary">Не удалось загрузить лицензию.</Typography.Paragraph>;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={!canEdit}
      initialValues={licenseFormValues(license)}
      onFinish={(values) =>
        onSave({
          licenseNumber: (values.licenseNumber || "").trim(),
          date: toDateValue(values.date),
          expirationDate: toDateValue(values.expirationDate),
          modules: values.modules || [],
          extraModules: values.extraModules || [],
          workplaces: values.workplaces || null,
          dealerWorkplaces: values.dealerWorkplaces || null,
          databases: values.databases || 1
        })
      }
    >
      {license.isExpired && (
        <Typography.Paragraph className="cabinet-license-expired">Срок лицензии истёк — модули в программе выключены.</Typography.Paragraph>
      )}
      {!license.fileExists && (
        <Typography.Paragraph type="secondary">Файл лицензии ещё не создан — сохраните, чтобы записать его.</Typography.Paragraph>
      )}
      <div className="cabinet-license-dates">
        <Form.Item name="licenseNumber" label="Номер лицензии">
          <Input placeholder="Номер" />
        </Form.Item>
        <Form.Item name="date" label="Дата выдачи">
          <DatePicker format={DATE_FORMAT} allowClear={false} />
        </Form.Item>
        <Form.Item name="expirationDate" label="Срок действия" extra="Пусто — бессрочная">
          <DatePicker format={DATE_FORMAT} allowClear placeholder="бессрочная" />
        </Form.Item>
        <Form.Item name="workplaces" label="Рабочие места" extra="Пусто — безлимитно. При лимите у пользователя одна активная сессия: новый вход закрывает остальные.">
          <InputNumber min={1} precision={0} placeholder="безлимитно" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="dealerWorkplaces"
          label="Дилерские лицензии (только расчет)"
          extra="Пусто или 0 — отдельного пула нет. Занимает пользователь без права на настройки (4) и партии (56)."
        >
          <InputNumber min={1} precision={0} placeholder="нет" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="databases" label="Базы данных" extra="Сколько баз можно завести. Пусто — 1.">
          <InputNumber min={1} precision={0} placeholder="1" style={{ width: "100%" }} />
        </Form.Item>
      </div>
      <LicenseModuleChecks name="modules" label="Основные модули" items={license.modules} />
      <LicenseModuleChecks name="extraModules" label="Дополнительные модули" items={license.extraModules} />
      {canEdit && (
        <div className="form-footer-actions">
          <Button type="primary" htmlType="submit" loading={saving}>
            Сохранить
          </Button>
        </div>
      )}
    </Form>
  );
};

const LicenseModuleChecks = ({ name, label, items }) => (
  <Form.Item name={name} label={label} className="cabinet-license-group">
    <Checkbox.Group className="cabinet-license-modules">
      {(items || []).map((item) => (
        <Checkbox key={item.id} value={item.id} className="cabinet-license-check">
          <span className="cabinet-license-name">{item.name}</span>
          {item.sections ? <span className="cabinet-license-sections">{item.sections}</span> : null}
        </Checkbox>
      ))}
    </Checkbox.Group>
  </Form.Item>
);

const InstanceLinks = ({ urls, fallback }) => {
  const list = visibleInstanceUrls(urls?.length ? urls : fallback ? [fallback] : []);
  if (!list.length) return null;

  return (
    <Typography.Paragraph className="cabinet-preview">
      {list.length === 1 ? "Адрес:" : "Адреса:"}{" "}
      {list.map((url, index) => (
        <span key={url}>
          {index > 0 && ", "}
          <a href={url} target="_blank" rel="noreferrer">
            {url}
          </a>
        </span>
      ))}
    </Typography.Paragraph>
  );
};

export default CabinetPage;
